import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useBridgeUpgradeOffer } from "@/state/hooks/useBridgeUpgradeOffer";
import { ENDPOINTS } from "@common/utils/url-registry";
import { useOfferSurfaceMounted } from "./offerSurface";
import { dismissBridgeUpgrade, useBridgeUpgradeDismissed } from "./bridgeUpgradeDismissal";
import { BRIDGE_UPGRADE_TAGLINE, bridgeUpgradeFinishCopy, bridgeUpgradeLead } from "./bridgeUpgradeCopy";
import { BridgeUpgradeFeatureIcon } from "./bridgeUpgradeIcons";
import BridgeUpgradeModal from "./BridgeUpgradeModal";

/**
 * Whether the viewer is already inside the install wizard. A clinic mid-flow does not need a strip
 * above it telling them their practice can now connect directly.
 *
 * Only the wizard is decided by route. The announcement card is not: a route list would need
 * updating whenever a surface is added, and nothing fails when it isn't. The card announces itself
 * instead — `offerSurface.ts`.
 */
const isOnBridgeSetupRoute = (pathname: string): boolean => pathname.startsWith(`/${ENDPOINTS.BridgeSetup}`);

/**
 * The app-wide strip for the HappyBridge upgrade offer. Mounted by `RootLayout`, above the routed
 * content and below the router, so it gets `useLocation`/`useNavigate` like any other component.
 * Renders null unless `useBridgeUpgradeOffer()` says the account is in the offer cohort.
 *
 * At the start stage the CTA opens `BridgeUpgradeModal` rather than navigating straight to the
 * wizard (finish stages do navigate straight there, see `finish` below). This strip is
 * seen by everyone on the account, not just the owner — most sessions are vets and techs on tablets
 * in exam rooms with no access to the practice's server — so sending them into an irreversible
 * connector flip they cannot finish is worse than a soft open into the modal, where booking a call
 * with our team is a real option.
 *
 * `min-h-[44px]` on both buttons, as on every button in this offer: 44px is the smallest reliable
 * touch target and this product is used on tablets propped up in exam rooms.
 */
export default function BridgeUpgradeBanner(): React.JSX.Element | null {
    const { eligible, integration, stage } = useBridgeUpgradeOffer();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const cardIsOnScreen = useOfferSurfaceMounted();
    const isDismissed = useBridgeUpgradeDismissed();
    const [modalOpen, setModalOpen] = useState(false);

    // A soft navigation, like the card's. This used to be `window.location.assign` deferred through
    // `runWhenRecorderIdle`, because a strip mounted above the router has no `useNavigate` and a
    // hard navigation tears the MediaRecorder down mid-capture — this strip is on screen during
    // appointments. Mounting inside the router removes the cause rather than guarding the symptom.
    const startUpgrade = () => navigate(`/${ENDPOINTS.BridgeSetup}`);

    if (!eligible || isDismissed || cardIsOnScreen || isOnBridgeSetupRoute(pathname)) return null;

    // The SAME sentence the announcement card leads with — `bridgeUpgradeLead` owns both, so a
    // reword reaches both surfaces or neither. Written to be READ IN PASSING: this is a strip
    // above the whole app, competing with a schedule the clinic actually came here for, and it is
    // all a phone shows (the detail line below is `hidden sm:block`).
    //
    // EN-1333: past "start" the lead is the task to finish instead. A finish stage navigates
    // directly rather than opening the modal: the remaining steps (rooms and staff) can be done
    // from any browser, and finishing the install is explained on the wizard's install screen.
    const finish =
        stage === "finishInstall" || stage === "finishSetup" ? bridgeUpgradeFinishCopy(stage, integration) : null;
    const lead = finish ? finish.lead : bridgeUpgradeLead(integration);

    return (
        // The wrapper CLIPS the drop-in: the strip animates from `translateY(-100%)`, and without
        // `overflow-hidden` that start frame paints over the banners above it in `app.tsx`. A
        // transform does not affect layout, so nothing below shifts while the animation runs.
        <div className="overflow-hidden">
            {/* Arrives rather than appears — `banner-drop-in` (tailwind.config.js) is a full-height
                drop, not `fade-slip-in`'s 4px nudge, because this strip is the topmost thing on
                screen and a 4px move there reads as a rendering hiccup. `motion-reduce:animate-none`
                because a clinic that has asked its OS for less motion should not get a moving
                advertisement; with the animation off the strip is simply already in place. */}
            <div className="animate-banner-drop-in motion-reduce:animate-none bg-max-25 border-t-[3px] border-t-max-500 border-b border-b-max-200 px-4 py-3.5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-3.5">
                {/* Sync, not a star: a star says "new", and the pill beside the headline
                    already says that in a word (at the start stage; finish stages show no pill). This is the one glyph that says INTEGRATION, which
                    is what the strip is actually about. Same icon set as the modal and the card —
                    `bridgeUpgradeIcons.tsx`, looked up by id, never by position. */}
                <span className="hidden sm:inline-flex flex-none h-10 w-10 items-center justify-center rounded-xl border border-max-200 bg-white text-max-925">
                    <BridgeUpgradeFeatureIcon id="sync" className="h-5 w-5" />
                </span>
                <div className="flex-grow min-w-0 flex flex-col gap-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[15px] font-bold text-shadow-900">{lead}</span>
                        {!finish && (
                            <span className="hidden sm:inline-flex flex-none items-center rounded-full bg-max-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-max-950">
                                New
                            </span>
                        )}
                    </div>
                    {/* Start stage only. "Five minutes" is the SETUP cost only, and this is the only surface giving a
                        number: the modal times the install (5 to 10 minutes) and the wizard says the
                        switchover happens once setup is finished
                        (the install step in `steps/InstallConnectStep.tsx`), which is where a clinic commits. A
                        teaser read in passing is not that place. First sentence is the shared
                        tagline — see `bridgeUpgradeCopy` for why it is worded as it is. */}
                    <span className="hidden sm:block text-[13px] leading-[18px] text-gray-500">
                        {finish ? finish.detail : <>{BRIDGE_UPGRADE_TAGLINE} Five minutes of your time to set up.</>}
                    </span>
                </div>
                {/* A worded dismiss rather than a bare ×. The × is right on the announcement CARD,
                    where the offer is plainly an overlay on the clinic's own numbers; here the strip
                    is the whole message, and "Not now" says what the control does — it snoozes the
                    offer for five days, it does not decline it. It also gives the keyboard and
                    screen-reader path a real label instead of an aria-label standing in for one. */}
                <div className="flex flex-none items-center justify-end gap-1 sm:contents">
                    <button
                        type="button"
                        className="focused flex-none rounded-md px-3 min-h-[44px] text-sm font-semibold text-gray-500 hover:text-shadow-900 transition-colors"
                        onClick={dismissBridgeUpgrade}
                    >
                        Not now
                    </button>
                    {/* `.primary-button` (index.css), not a hand-rolled one. This used to be `bg-max-700`
                    with no border, no hover, no focus ring and an arbitrary `text-[13px]` — a button
                    that looked like the app's without behaving like it. `min-h-[44px]` still overrides
                    the class's `py-1.5`, which lands around 32px: the shared class is the look, the
                    touch target is this product's own rule for exam-room tablets. */}
                    <button
                        type="button"
                        className="primary-button flex-none whitespace-nowrap min-h-[44px]"
                        onClick={finish ? startUpgrade : () => setModalOpen(true)}
                    >
                        {finish ? finish.cta : "Learn More"}
                    </button>
                </div>
                {!finish && (
                    <BridgeUpgradeModal
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        onStartUpgrade={startUpgrade}
                        integration={integration}
                    />
                )}
            </div>
        </div>
    );
}
